import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  X, Buildings, MapPin, ChartLineUp, ShieldCheck, Spinner,
  CheckCircle, Warning, XCircle, ArrowsClockwise, LockKey,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

// Gauge semicircolare stile Cerved (score 0-100)
const ScoreGauge = ({ score }) => {
  const s = Math.max(0, Math.min(100, score ?? 0));
  const color = s < 30 ? '#dc2626' : s < 50 ? '#d97706' : '#059669';
  const R = 70, CX = 90, CY = 88;
  const angle = Math.PI * (1 - s / 100);
  const x = CX + R * Math.cos(angle), y = CY - R * Math.sin(angle);
  const large = s > 50 ? 1 : 0;
  return (
    <div className="flex flex-col items-center" data-testid="credit-score-gauge">
      <svg width="180" height="105" viewBox="0 0 180 105">
        <path d={`M ${CX - R} ${CY} A ${R} ${R} 0 0 1 ${CX + R} ${CY}`}
              fill="none" stroke="#e2e8f0" strokeWidth="14" strokeLinecap="round" />
        {s > 0 && (
          <path d={`M ${CX - R} ${CY} A ${R} ${R} 0 ${large} 1 ${x} ${y}`}
                fill="none" stroke={color} strokeWidth="14" strokeLinecap="round" />
        )}
        <text x={CX} y={CY - 14} textAnchor="middle" fontSize="30" fontWeight="800" fill={color}>{s}</text>
        <text x={CX} y={CY + 4} textAnchor="middle" fontSize="10" fill="#94a3b8">SCORE / 100</text>
      </svg>
      <div className="flex gap-3 text-[9px] text-slate-400 -mt-1">
        <span><span className="inline-block w-2 h-2 rounded-full bg-red-600 mr-1" />0-29 Bloccato</span>
        <span><span className="inline-block w-2 h-2 rounded-full bg-amber-600 mr-1" />30-49 Review</span>
        <span><span className="inline-block w-2 h-2 rounded-full bg-emerald-600 mr-1" />50+ Approvato</span>
      </div>
    </div>
  );
};

const DecisionPill = ({ decision }) => {
  const cfg = {
    approved: { cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40', icon: <CheckCircle size={13} weight="fill" />, label: 'APPROVATO' },
    base_ok: { cls: 'bg-sky-500/20 text-sky-300 border-sky-500/40', icon: <CheckCircle size={13} />, label: 'P.IVA ATTIVA' },
    manager_review: { cls: 'bg-amber-500/20 text-amber-300 border-amber-500/40', icon: <Warning size={13} weight="fill" />, label: 'CHECK MANAGERIALE' },
    blocked: { cls: 'bg-red-500/20 text-red-300 border-red-500/40', icon: <XCircle size={13} weight="fill" />, label: 'BLOCCATO' },
  }[decision];
  if (!cfg) return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-slate-500/40 bg-slate-500/20 text-slate-300 text-[11px] font-bold tracking-wide">
      DA VERIFICARE
    </span>
  );
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-bold tracking-wide ${cfg.cls}`}>
      {cfg.icon} {cfg.label}
    </span>
  );
};

const InfoField = ({ label, value, mono }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
    <div className={`text-sm text-slate-800 mt-0.5 ${mono ? 'font-mono' : 'font-medium'}`}>{value || '—'}</div>
  </div>
);

const eur = (v) => (typeof v === 'number' && v !== 0) ? `€ ${v.toLocaleString('it-IT')}` : null;

export default function CreditReportModal({ piva, onClose, onUpdated }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(null); // 'base' | 'full'

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/credit-check/report/${piva}`, { credentials: 'include' });
      if (res.ok) setReport(await res.json());
      else toast.error('Errore caricamento scheda');
    } catch (e) { toast.error('Errore di rete'); }
    finally { setLoading(false); }
  }, [piva]);

  useEffect(() => { load(); }, [load]);

  const refresh = async (full) => {
    setRefreshing(full ? 'full' : 'base');
    try {
      const res = await fetch(`${API}/api/credit-check/report/${piva}/refresh`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ full }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setReport(d);
        toast.success(full ? 'Dati completi aggiornati' : 'Verifica aggiornata');
        onUpdated && onUpdated();
      } else toast.error(d.detail || 'Errore aggiornamento');
    } catch (e) { toast.error('Errore di rete'); }
    finally { setRefreshing(null); }
  };

  if (!piva) return null;
  const d = report?.data || {};
  const hasFin = typeof d.score === 'number';

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4"
         onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto"
           data-testid="credit-report-modal">

        {/* HEADER scuro stile Cerved */}
        <div className="bg-slate-900 text-white px-6 py-5 rounded-t-2xl relative">
          <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-white/10 text-slate-300"
                  data-testid="credit-report-close">
            <X size={18} />
          </button>
          <div className="text-[10px] uppercase tracking-[3px] text-orange-400 font-bold mb-1.5 flex items-center gap-1.5">
            <ShieldCheck size={13} weight="fill" /> Scheda di Credito
          </div>
          <h2 className="text-xl font-bold leading-tight pr-10">
            {loading ? 'Caricamento…' : (report?.ragione_sociale || d.ragione_sociale || `P.IVA ${piva}`)}
          </h2>
          <div className="flex items-center gap-3 mt-2.5 flex-wrap">
            <span className="font-mono text-sm text-slate-300">P.IVA {piva}</span>
            {!loading && <DecisionPill decision={d.decision} />}
            {d.stato_attivita && (
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${d.stato_attivita === 'ATTIVA' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}>
                {d.stato_attivita}
              </span>
            )}
          </div>
        </div>

        {loading ? (
          <div className="p-14 text-center text-slate-400"><Spinner size={26} className="animate-spin inline" /></div>
        ) : (
          <div className="p-6 space-y-6">
            {/* SCORE + esito */}
            <div className="flex items-center gap-6 flex-wrap">
              {hasFin ? <ScoreGauge score={d.score} /> : (
                <div className="flex-1 min-w-[240px] rounded-xl border-2 border-dashed border-slate-200 p-5 text-center">
                  <LockKey size={26} className="mx-auto text-slate-300 mb-2" />
                  <div className="text-sm font-semibold text-slate-600">Score e dati finanziari non ancora richiesti</div>
                  <div className="text-xs text-slate-400 mt-1">
                    I dati completi (bilanci, score 0-100) sono a pagamento e richiedibili da un profilo senior/admin.
                  </div>
                </div>
              )}
              {d.reason && (
                <div className="flex-1 min-w-[200px] text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <span className="font-semibold text-slate-800">Esito: </span>{d.reason}
                </div>
              )}
            </div>

            {/* ANAGRAFICA */}
            <div>
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200 pb-1.5 mb-3">
                <Buildings size={14} /> Anagrafica
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <InfoField label="Denominazione" value={report?.ragione_sociale || d.ragione_sociale} />
                <InfoField label="Partita IVA" value={piva} mono />
                <InfoField label="Stato attività" value={d.stato_attivita} />
                <InfoField label="Sede" value={[d.indirizzo, d.comune, d.provincia ? `(${d.provincia})` : ''].filter(Boolean).join(', ')} />
                <InfoField label="Codice ATECO" value={d.ateco} mono />
                {report?.source === 'lead' && <InfoField label="Stato nel CRM" value={report?.lead_status} />}
              </div>
            </div>

            {/* DATI FINANZIARI */}
            <div>
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200 pb-1.5 mb-3">
                <ChartLineUp size={14} /> Dati finanziari {d.balance_year ? `(bilancio ${d.balance_year})` : ''}
              </div>
              {hasFin ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="credit-report-financials">
                  <InfoField label="Fatturato" value={eur(d.fatturato)} />
                  <InfoField label="Patrimonio netto" value={eur(d.equity)} />
                  <InfoField label="Capitale sociale" value={eur(d.share_capital)} />
                  <InfoField label="Dipendenti" value={d.dipendenti > 0 ? d.dipendenti : null} />
                  <InfoField label="Trend fatturato" value={typeof d.turnover_trend === 'number' && d.turnover_trend !== 0 ? `${d.turnover_trend > 0 ? '+' : ''}${d.turnover_trend}%` : null} />
                  <InfoField label="ROE" value={typeof d.roe === 'number' && d.roe !== 0 ? `${d.roe}%` : null} />
                  <InfoField label="EBITDA" value={eur(d.ebitda)} />
                  <InfoField label="Dimensione" value={d.enterprise_size} />
                </div>
              ) : (
                <div className="text-sm text-slate-400 italic">Non disponibili — richiedi i dati completi qui sotto.</div>
              )}
              {d.upsell_cerved && (
                <div className="mt-3 text-xs bg-indigo-50 border border-indigo-200 text-indigo-800 rounded-lg px-3 py-2">
                  💡 Fatturato &gt; €50.000: per questo soggetto è consigliata una visura di approfondimento <strong>Cerved</strong>.
                </div>
              )}
            </div>

            {/* FOOTER: verifica + azioni */}
            <div className="flex items-center justify-between flex-wrap gap-3 pt-3 border-t border-slate-200">
              <div className="text-[11px] text-slate-400">
                {d.checked_at
                  ? <>Ultima verifica: {new Date(d.checked_at).toLocaleString('it-IT')} · {d.checked_by || '—'}</>
                  : 'Mai verificata'}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => refresh(false)} disabled={!!refreshing}
                        data-testid="credit-report-refresh-base">
                  {refreshing === 'base' ? <Spinner size={14} className="animate-spin mr-1" /> : <ArrowsClockwise size={14} className="mr-1" />}
                  Verifica base (gratuita)
                </Button>
                {report?.can_full && (
                  <Button size="sm" onClick={() => refresh(true)}
                          disabled={!!refreshing || !report?.allow_paid_company}
                          className="bg-slate-900 hover:bg-slate-800 text-white"
                          title={report?.allow_paid_company ? 'Score 0-100 + bilanci (IT-full, a pagamento)' : 'Attiva le chiamate a pagamento in Impostazioni → Openapi'}
                          data-testid="credit-report-refresh-full">
                    {refreshing === 'full' ? <Spinner size={14} className="animate-spin mr-1" /> : <ChartLineUp size={14} className="mr-1" />}
                    Richiedi dati completi
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
